import { ArrowUpDown, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import type { OrderSortField, SortDirection } from "./prosthesis-orders-sort";

const OPCOES: { valor: `${OrderSortField}:${SortDirection}`; rotulo: string; curto: string }[] = [
  { valor: "createdAt:desc", rotulo: "Mais recentes", curto: "Recentes" },
  { valor: "createdAt:asc", rotulo: "Mais antigos", curto: "Antigos" },
  { valor: "deadline:asc", rotulo: "Prazo mais próximo", curto: "Prazo próximo" },
  { valor: "deadline:desc", rotulo: "Prazo mais distante", curto: "Prazo distante" },
  { valor: "patient:asc", rotulo: "Paciente (A–Z)", curto: "Paciente" },
  { valor: "lab:asc", rotulo: "Laboratório (A–Z)", curto: "Laboratório" },
  { valor: "status:asc", rotulo: "Status", curto: "Status" },
];

type Props = {
  campo: OrderSortField;
  direcao: SortDirection;
  onChange: (campo: OrderSortField, direcao: SortDirection) => void;
  /** Celular: só o ícone e o critério curto no botão; a lista aberta continua com os nomes inteiros. */
  compacto?: boolean;
  className?: string;
};

/**
 * "Ordenar por" da lista de pedidos — vale pra lista e pros cartões. Seletor nativo: no celular abre
 * o seletor do sistema, que é o mais fácil de acertar com o dedo. Clicar no título de uma coluna da
 * tabela (desktop) também ordena; combinação que não está na lista aparece como "Pela coluna".
 */
export function OrdenarPedidos({ campo, direcao, onChange, compacto, className }: Props) {
  const atual = `${campo}:${direcao}`;
  const opcao = OPCOES.find((o) => o.valor === atual);

  const select = (
    <select
      aria-label="Ordenar por"
      value={atual}
      onChange={(e) => {
        const [c, d] = e.target.value.split(":") as [OrderSortField, SortDirection];
        onChange(c, d);
      }}
      className={
        compacto
          ? "absolute inset-0 h-full w-full cursor-pointer opacity-0"
          : "h-full min-w-0 flex-1 cursor-pointer appearance-none rounded-md bg-transparent pl-2 pr-8 text-[15px] font-medium text-zinc-800 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:text-[13px] dark:text-foreground"
      }
    >
      {!opcao && <option value={atual}>Pela coluna</option>}
      {OPCOES.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.rotulo}
        </option>
      ))}
    </select>
  );

  if (compacto) {
    // O seletor fica invisível por cima do botão: o toque abre a lista nativa, e o botão mostra o
    // critério curto — "Ordenar por Mais recentes" não cabe ao lado de Status e da visualização.
    return (
      <label
        className={cn(
          "relative flex h-11 items-center justify-between gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-[13px] font-semibold text-zinc-700 dark:border-border dark:bg-card dark:text-foreground",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <ArrowUpDown className="h-4 w-4 shrink-0 text-zinc-400" />
          <span className="truncate">{opcao?.curto ?? "Coluna"}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        {select}
      </label>
    );
  }

  return (
    <label className="relative flex h-12 w-full items-center rounded-md border border-zinc-200 bg-white text-sm sm:h-9 sm:w-auto dark:border-border dark:bg-card">
      <span className="pointer-events-none flex shrink-0 items-center gap-1.5 pl-3 text-[13px] text-zinc-500 sm:text-xs dark:text-muted-foreground">
        <ArrowUpDown className="h-4 w-4 text-zinc-400" />
        Ordenar por
      </span>
      {select}
      <ChevronDown aria-hidden className="pointer-events-none absolute right-3 h-4 w-4 text-zinc-400" />
    </label>
  );
}
